<script lang="ts" context="module">
    import { generateHotKeyTapStore } from "@utils/underhood/registerHotkey"
    import { writable } from "svelte/store"
    export const paletteOpen = generateHotKeyTapStore("h", false)

    const placement = writable({
        snap: "right" as "left" | "right",
        x: 20,
        y: 200,
    })
</script>

<script lang="ts">
    import Icon from "@components/material/Icon.svelte"
    import { highlightersSorted, selectedHighlighter, highlighters } from "@store/highlights"
    import { highlightMode } from "@utils/highlights/highlightShortcuts"
    import { fade } from "svelte/transition"
    import { clickEnter } from "@utils/directives/accessibility"
    import { darkmode } from "@utils/material/darkmode"

    // Highlighters loaded?

    let dragging = false
    let original: { clientX: number; clientY: number } | null = null
    let transformOnBeginDrag: { x: number; y: number } | null = null
    function dragstart(e: PointerEvent) {
        e.stopPropagation()
        original = e
        transformOnBeginDrag = { ...$placement }
        dragging = true
    }

    let swapNext: "left" | "right" | null = null
    async function drag(e: PointerEvent) {
        if (!dragging) return
        const direction = swapNext || $placement.snap
        if (!original) throw new Error("Original position not set")
        if (!transformOnBeginDrag) throw new Error("Transform on begin drag not set")
        const delta = {
            x: e.clientX - original.clientX,
            y: e.clientY - original.clientY,
        }
        const transformDelta = {
            x: transformOnBeginDrag.x + (direction == "left" ? delta.x : -delta.x),
            y: transformOnBeginDrag.y + delta.y,
        }

        $placement = {
            x: transformDelta.x,
            y: transformDelta.y,
            snap: direction,
        }

        swapNext = null

        // Snap logic
        const rect = palette.getBoundingClientRect()
        const left = rect.left
        const right = window.innerWidth - rect.right

        const closerToRight = right < left
        if (closerToRight && $placement.snap == "left") {
            swap(rect)
            swapNext = "right"
        }
        if (!closerToRight && $placement.snap == "right") {
            swap(rect)
            swapNext = "left"
        }
    }

    function swap(d: DOMRect) {
        if (!transformOnBeginDrag) throw new Error("Transform on begin drag not set")

        // Simply find the equivaleent on the other side
        transformOnBeginDrag = {
            ...transformOnBeginDrag,
            x: window.innerWidth - transformOnBeginDrag.x - d.width,
        }
    }

    async function dragend(e: PointerEvent) {
        if (!dragging) return
        original = null
        dragging = false
        transformOnBeginDrag = null
    }

    let palette: HTMLElement
</script>

<svelte:window on:pointerup={dragend} on:pointermove={drag} />
<div
    transition:fade|global
    bind:this={palette}
    class="palette"
    style={`top: ${$placement.y}px; ${$placement.snap}: max(${$placement.x}px, 0px)`}
>
    <div class="palette--draggable" class:reversed={$placement.snap == "left"}>
        <div on:pointerdown={dragstart} class="palette--draggable-grip">
            <Icon icon="drag" />
        </div>
        <div class="palette--draggable-bubbles">
            {#each [...$highlightersSorted.entries()] as [id, highlighter], i (id)}
                <div class="palette--draggable-bubble" class:selected={$selectedHighlighter == id}>
                    <span class="label-container secondaryFont" class:visible={$highlightMode}>
                        <span class="number">
                            {i + 1}. &nbsp;
                        </span>
                        <span class="label">{highlighter.label}</span>
                    </span>
                    <span
                        role="link"
                        aria-label="select highlighter {highlighter.label ? highlighter.label : i}"
                        tabindex={0}
                        on:keydown={clickEnter}
                        on:click|preventDefault={() => ($selectedHighlighter = id)}
                        style:background={$darkmode
                            ? $highlighters.get(id)?.dark
                            : $highlighters.get(id)?.light}
                    />
                </div>
            {/each}
        </div>
    </div>
</div>
